/**
 * VerifiedConstraints — TASK-686.
 *
 * The trust artefact on a proposal: which rails were actually checked, with the numbers.
 * An RM reading "this respects the client's ESG preferences" has to take the model's word for
 * it. An RM reading "Foreign (Dev. Markets) would move to 12.85% vs a 12.70% target — drift
 * +0.150pp against a ±2.0pp band" does not.
 *
 * Three verdicts, three visual weights, and the third one is the point:
 *   pass        — checked and holds.
 *   breach      — checked and fails.
 *   not_checked — the input wasn't available. Shown in its own muted style and never folded
 *                 into "pass": a client with no target allocation has UNKNOWN drift, and
 *                 rendering that as a green tick is the lie this component exists to prevent.
 *   shortfall   — TASK-870. Checked, and the answer is a number that falls short of what was
 *                 asked for: "these lines raise CHF 172,384, which is CHF 211,615 short of the
 *                 CHF 384,000 asked for". Not a breach — a proposal that raises less than
 *                 requested is a fact about the book, not a rule violation — and emphatically
 *                 NOT "not checked", which is where it landed before this case existed and is
 *                 the exact confusion the paragraph above forbids.
 *
 * Shared by every proposal surface (BeforeAfter, CockpitPortfolioView, PortfolioComposer) —
 * one renderer, so the three cannot disagree about what a breach looks like.
 */

import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';

export type ConstraintVerdict = 'pass' | 'breach' | 'not_checked' | 'shortfall';

export interface VerifiedConstraint {
  constraint: string;
  verdict: ConstraintVerdict;
  detail: string;
  checked_value?: string | number | null;
  limit?: string | number | null;
  subject?: string | null;
  hard?: boolean;
}

export interface RiskDelta {
  sold?: string | null;
  bought?: string | null;
  sold_vol_pct: number;
  bought_vol_pct: number;
  delta_pct: number;
  /** The honesty flag. When true the numbers are a class band, not a measurement. */
  estimated: boolean;
  basis?: string;
}

const CONSTRAINT_LABELS: Record<string, string> = {
  cio_universe: 'CIO approved list',
  value_conflict: 'Client value exclusions',
  mandate_drift: 'Mandate drift',
  verification: 'Verification',
  // TASK-870. Without an entry a row renders its raw snake_case key at the advisor.
  holdings: 'Held by this client',
  sell_direction: 'Sells from an overweight slot',
  proposal_total: 'Total raised',
};

const VERDICT_STYLE: Record<ConstraintVerdict, { chip: string; icon: JSX.Element; label: string }> = {
  pass: {
    chip: 'bg-green-50 text-green-700 border-green-200',
    icon: <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-green-600" />,
    label: 'Verified',
  },
  breach: {
    chip: 'bg-red-50 text-red-700 border-red-200',
    icon: <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-red-600" />,
    label: 'Breach',
  },
  not_checked: {
    chip: 'bg-gray-50 text-gray-600 border-gray-200',
    icon: <HelpCircle className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />,
    label: 'Not checked',
  },
  shortfall: {
    chip: 'bg-amber-50 text-amber-800 border-amber-200',
    icon: <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-600" />,
    label: 'Short of target',
  },
};

export interface VerifiedConstraintsProps {
  constraints?: VerifiedConstraint[] | null;
  riskDeltas?: RiskDelta[] | null;
  className?: string;
}

export function VerifiedConstraints({
  constraints,
  riskDeltas,
  className = '',
}: VerifiedConstraintsProps) {
  const hasConstraints = Array.isArray(constraints) && constraints.length > 0;
  const hasRisk = Array.isArray(riskDeltas) && riskDeltas.length > 0;

  // Absent, not empty: a proposal created before verification existed says nothing here
  // rather than claiming "0 constraints checked".
  if (!hasConstraints && !hasRisk) return null;

  return (
    <div data-testid="verified-constraints" className={`space-y-2 ${className}`}>
      {hasConstraints && (
        <>
          <p className="text-xs font-medium text-gray-500">Checks run on these trades</p>
          <ul className="space-y-1">
            {constraints!.map((c, i) => {
              const style = VERDICT_STYLE[c.verdict] ?? VERDICT_STYLE.not_checked;
              return (
                <li
                  key={`${c.constraint}-${c.subject ?? i}`}
                  data-testid={`constraint-${c.verdict}`}
                  className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs ${style.chip}`}
                >
                  {style.icon}
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">
                      {CONSTRAINT_LABELS[c.constraint] ?? c.constraint}
                    </span>
                    {c.subject && <span className="opacity-70"> · {c.subject}</span>}
                    <span className="mx-1 opacity-50">—</span>
                    {/* The figure and the limit, verbatim from the server. The model does not
                        restate these in prose (system-prompt hard rule 2). */}
                    <span>{c.detail}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {hasRisk && (
        <>
          <p className="pt-1 text-xs font-medium text-gray-500">Risk change per substitution</p>
          <ul className="space-y-1">
            {riskDeltas!.map((r, i) => (
              <li
                key={`${r.sold}-${r.bought}-${i}`}
                data-testid="risk-delta"
                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700"
              >
                <span className="font-medium">
                  {r.sold ?? '?'} → {r.bought ?? '?'}
                </span>
                <span>
                  volatility {r.sold_vol_pct}% → {r.bought_vol_pct}%
                </span>
                <span
                  className={
                    r.delta_pct > 0 ? 'font-medium text-amber-700' : 'font-medium text-green-700'
                  }
                >
                  {r.delta_pct > 0 ? '+' : ''}
                  {r.delta_pct}pp
                </span>
                {/* Never let an estimate read as a measurement. */}
                {r.estimated && (
                  <span
                    data-testid="risk-delta-estimated"
                    title={r.basis ?? 'sub-asset-class estimate'}
                    className="rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800"
                  >
                    est.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default VerifiedConstraints;
